import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Layers, Wand2, Cpu, Bot, Image as ImageIcon, Video, Palette, Brush, Pen, Rocket } from "lucide-react";
import Breadcrumb from "../components/Breadcrumb";
import SectionLabel from "../components/SectionLabel";
import ScrollReveal from "../components/ScrollReveal";
import { SKILLS, AI_ADVANTAGE, BADGES } from "../lib/data";

const ICONS = { Sparkles, Layers, Wand2, Cpu, Bot, ImageIcon, Video, Palette, Brush, Pen, Rocket };
const SKILL_ICONS = ["Palette", "Pen", "ImageIcon", "Video", "Layers", "Sparkles", "Wand2", "Bot", "Cpu", "Rocket", "Brush", "Pen"];

const TIMELINE = [
  {
    emoji: "🚀",
    tag: "First Venture · 2020 – 2025",
    title: "EditX Studio",
    sub: "Co-founded with RM Group · Web · Branding · UI/UX · Creative Media",
    body: "Built and ran my first company during college. Served 30+ clients across Web Development, Branding, Photography, Videography and Digital Presence. Partnership challenges led to closure in 2025 — but earned me hands-on exposure to client work, team management and business operations.",
  },
  {
    emoji: "💼",
    tag: "Industry Experience",
    title: "Tech Mahindra & Devstree IT Services Pvt. Ltd.",
    sub: "Frontend · MERN Stack · UI/UX · Branding · AI Integration",
    body: "Worked with established companies, further sharpening skills across the full development and design stack — building real products for real teams.",
  },
  {
    emoji: "🌟",
    tag: "Now",
    title: "Sinix Studio",
    sub: "Modern Web · AI-Powered · Brand Identity · Creative Development",
    body: "Sinix Studio is the evolution — a refined, experience-driven studio combining modern web development, AI-powered workflows and brand identity systems. Team-driven. Focused. Fully accountable.",
    highlight: true,
  },
];

export default function About() {
  return (
    <div className="page" data-testid="about-page">

      {/* Hero */}
      <section className="page-hero">
        <div className="container-x">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "About", to: "/about" }]} />
          <h1 style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <img src="/logo.png" alt="Sinix Studio" style={{ height: 48, width: "auto" }} />
            <span>Creative. Focused. <span style={{ color: "var(--slate-grey)", fontStyle: "italic", fontWeight: 400 }}>AI-Powered.</span></span>
          </h1>
          <p className="lead">
            Sinix Studio is a creative team based in Mehsana — combining traditional design craft
            with AI-powered tooling to ship work that's faster, smarter and better than agencies twice the size.
          </p>
          <div className="badge-row">
            {BADGES.map((b) => <span key={b} className="pill">{b}</span>)}
          </div>
        </div>
      </section>

      {/* Studio Story */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={1}>Studio Story</SectionLabel>
          <div className="about-grid">
            <ScrollReveal>
              <h2 className="sec-title">A studio built for results.</h2>
              <p className="sec-sub" style={{ marginTop: 24 }}>
                We built Sinix Studio because the best design work comes from a tight, focused team obsessing
                over the details — not a bloated committee diluting them. With 4+ years of work, we've partnered
                with founders launching their first product, D2C brands rebranding for scale, and SaaS teams sharpening their interfaces.
              </p>
              <p style={{ color: "var(--slate-grey)", fontSize: 16, lineHeight: 1.7, marginTop: 18 }}>
                The "AI-powered" part isn't marketing. Our team genuinely uses Midjourney, Firefly, Runway and Claude
                every single day. They make us 2× faster on execution so we can spend that time on the
                decisions that actually shape the work — strategy, hierarchy, polish.
              </p>
              <p style={{ color: "var(--slate-grey)", fontSize: 16, lineHeight: 1.7, marginTop: 18 }}>
                If you're looking for a creative partner who picks up the phone, ships on time, and treats your
                project like their own — let's talk.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <div className="stats-2x2">
                <div className="stat-mini"><div className="num">4+</div><div className="lbl">Years Exp.</div></div>
                <div className="stat-mini"><div className="num">30+</div><div className="lbl">Clients</div></div>
                <div className="stat-mini"><div className="num">100%</div><div className="lbl">Satisfaction</div></div>
                <div className="stat-mini"><div className="num">2×</div><div className="lbl">AI Speed</div></div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* My Journey */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={2}>Our Story</SectionLabel>
          <h2 className="sec-title">The story so far.</h2>
          <p className="sec-sub">From a curious student to a creative studio — here's how it happened.</p>

          {/* Photo + Intro */}
          <div className="story-hero-row">
            <ScrollReveal>
              <div className="story-photo-wrap">
                <img src="/shubham.jpg" alt="Shubham Prajapati" />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <div className="story-intro">
                <h3 className="story-intro-name">Shubham Prajapati</h3>
                <p className="story-intro-role">Developer · Designer · Creative Founder · Mehsana, India</p>
                <div className="story-stats-row">
                  {[
                    { num: "4+",  lbl: "Years Experience" },
                    { num: "30+", lbl: "Clients Served" },
                  ].map((s) => (
                    <div key={s.lbl} className="story-stat">
                      <div className="story-stat-num">{s.num}</div>
                      <div className="story-stat-lbl">{s.lbl}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Timeline */}
          <div className="story-timeline">
            {TIMELINE.map((item, i) => (
              <ScrollReveal key={i} delay={i * 80}>
                <div className={`story-timeline-item${item.highlight ? " highlight" : ""}`}>
                  <div className="story-timeline-dot">{item.emoji}</div>
                  <div className="story-timeline-content">
                    <div className="story-tag">{item.tag}</div>
                    <h4 className="story-title">{item.title}</h4>
                    <p className="story-sub">{item.sub}</p>
                    <p className="story-body">{item.body}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={3}>Skills &amp; Tools</SectionLabel>
          <h2 className="sec-title">The toolkit.</h2>
          <p className="sec-sub">Design fundamentals plus the AI stack that makes them ship faster.</p>
          <div className="skills-grid">
            {SKILLS.map((sk, i) => {
              const Ic = ICONS[SKILL_ICONS[i] || "Palette"];
              return (
                <ScrollReveal key={sk.name} delay={i * 40}>
                  <div className="skill-card" data-testid={`skill-${sk.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="ic"><Ic size={18} /></div>
                    <h5>{sk.name}</h5>
                    <span className="level">{sk.level}</span>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Advantage */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={4}>Design × AI</SectionLabel>
          <h2 className="sec-title">Why AI changes the game.</h2>
          <p className="sec-sub">Not gimmicks. Real workflow upgrades that translate into better work for clients.</p>
          <div className="ai-grid">
            {AI_ADVANTAGE.map((a, i) => (
              <ScrollReveal key={a.num} delay={i * 100}>
                <div className="ai-card" data-testid={`ai-${a.num}`}>
                  <div className="num">// {a.num}</div>
                  <h3>{a.t}</h3>
                  <p>{a.d}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="container-x">
        <div className="cta-dark">
          <div className="lead">Ready to start</div>
          <h2>Let's build something the rest of the market notices.</h2>
          <Link to="/contact" className="btn btn-light">Get In Touch <ArrowRight size={16} /></Link>
        </div>
      </div>

    </div>
  );
}
